import React, { useEffect, useState } from 'react';
import { Button, Row, Col, Alert } from 'reactstrap';
import * as Yup from 'yup';
import Helmet from 'react-helmet';
import { Redirect, Link, useParams, useHistory } from 'react-router-dom';
import { Field, useSession, Form } from '@cordata/buffet';

import { baseInstance } from '~/utils';

import { Splash } from '~/components';

const Activation = () => {
  const params = useParams();
  const history = useHistory();
  const [mobileNumber, setMobileNumber] = useState();
  const [passwordPolicy, setPasswordPolicy] = useState(Yup.string().required());
  const [passwordPolicyHelpText, setPasswordPolicyHelpText] = useState([]);
  const { isAuthed } = useSession();

  useEffect(() => {
    baseInstance
      .get(`/${params.id}/mobile-phone`)
      .then(r => r.data.data)
      .then(r => setMobileNumber(r.attributes.phoneNumber));

    baseInstance
      .get(`/password-policy?activation-uid=${params.id}`)
      .then(r => r.data)
      .then((r) => {
        let newPolicy = passwordPolicy;
        const newPolicyHelpText = passwordPolicyHelpText;

        if (r.passwordMinLength.value) {
          newPolicy = newPolicy.min(r.passwordMinLength.value);
          newPolicyHelpText.push(`${r.passwordMinLength.value} characters`);
        }

        if (r.passwordRequiresLowercase.value) {
          newPolicy = newPolicy.matches(/^.*[a-z].*$/, 'must contain a lowercase character');
          newPolicyHelpText.push('a lowercase character');
        }

        if (r.passwordRequiresUppercase.value) {
          newPolicy = newPolicy.matches(/^.*[A-Z].*$/, 'must contain an uppercase character');
          newPolicyHelpText.push('an uppercase character');
        }

        if (r.passwordRequiresNumber.value) {
          newPolicy = newPolicy.matches(/^.*\d.*$/, 'must contain a number');
          newPolicyHelpText.push('a number');
        }

        if (r.passwordRequiresSpecial.value) {
          newPolicy = newPolicy.matches(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~].*$/, 'must contain a symbol');
          newPolicyHelpText.push('a symbol');
        }

        setPasswordPolicy(newPolicy);
        setPasswordPolicyHelpText(newPolicyHelpText);
      });
  }, []);

  return isAuthed ? (
    <Redirect to="/home" />
  ) : (
    <Splash>
      <Helmet title="Activate my account" />

      <Form
        displayName="ActivationForm"
        enableReinitialize

        initialValues={{
          id: params.id || '',
          code: '',
          password: '',
          passwordConfirm: '',
          mobileNumber,
        }}

        onSubmit={(values, { setErrors, setSubmitting }) => {
          baseInstance
            .post('/reset-password', {
              'activation-uid': values.id,
              'organization-code': values.code,
              password: values.password,
              'mobile-phone':
                values.mobileNumber && values.mobileNumber !== ''
                  ? values.mobileNumber.replace(/\D/g, '')
                  : null,
            })
            .then((r) => {
              localStorage.setItem('session', JSON.stringify(r));

              history.push('/home');
            })
            .catch(({ response: { data: { errors } } }) => {
              let er = {};

              if (errors) {
                er = Object.values(errors)[0]
                  .replace(/\n/g, ',')
                  .split(',')
                  .reduce((a, m) => {
                    const [key, value] = m.split(':');

                    // eslint-disable-next-line
                    a[key] = value;

                    return a;
                  }, {});
              }

              setErrors({
                code: er.organization_code,
                password: er.password,
                server: errors,
              });
            })
            .then(() => setSubmitting(false));
        }}

        validationSchema={Yup.object().shape({
          code: Yup.string().required(),
          password: passwordPolicy,
          passwordConfirm: Yup.string()
            .oneOf([Yup.ref('password'), null], "doesn't match")
            .required(),
        })}

        formAlertsProps={{
          loginMessage: 'We are unable to complete this action. Please contact your Cordata Support Representative.',
          login: true,
        }}
      >
        {({
          errors,
          isSubmitting,
        }) => (
          <>
            {errors.data && errors.data.includes('expired') && (
              <Alert color="danger">Activation has Expired</Alert>
            )}

            <Field
              name="code"
              placeholder="Enter your code"
              autocomplete="new-password"
              required
              label={false}
            />

            <Field
              name="password"
              placeholder="Enter your password"
              type="password"
              autocomplete="new-password"
              text={`must contain: ${passwordPolicyHelpText.join(', ')}`}
              required
              label={false}
            />

            <Field
              name="passwordConfirm"
              placeholder="Enter your new password again"
              type="password"
              autocomplete="new-password"
              required
              label={false}
            />

            <Field
              name="mobileNumber"
              type="tel"
              autocomplete="new-password"
              text="Your mobile number can be used to unlock your account if you forget your password. This is the current mobile number we have on file. If you would like to update it now, please enter your mobile number."
              label={false}
            />

            <Row form>
              <Col xs="auto">
                <Button
                  disabled={isSubmitting}
                  outline
                  color="light"
                  block
                  tag={Link}
                  to="/login"
                >
                  Cancel
                </Button>
              </Col>

              <Col>
                <Button type="submit" disabled={isSubmitting} block>
                  {isSubmitting ? 'Activating ...' : 'Activate'}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Splash>
  );
};

export default Activation;
